import { useEffect, useRef } from 'react'
import { Wrapper, Status } from '@googlemaps/react-wrapper'
import { GOOGLE_MAPS_API_KEY } from '../common/constants'

// Small read-only map showing a single device's location.
//
// This used to be a `https://www.google.com/maps?...&output=embed` iframe, which
// the production CSP blocks (`frame-src 'self'`). It uses the same Maps JS API
// the Map page already loads, so no extra CSP surface is needed.

interface Props {
  lat: number
  lng: number
  title: string
  zoom?: number
}

const DEFAULT_ZOOM = 14

function StaticMap({ lat, lng, title, zoom = DEFAULT_ZOOM }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<any>(null)

  useEffect(() => {
    if (!ref.current) return

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(ref.current, {
        mapId: 'IOAG_MAP_ID',
        center: { lat, lng },
        zoom,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      })
    } else {
      mapRef.current.setCenter({ lat, lng })
      mapRef.current.setZoom(zoom)
    }

    // Reuse a single marker so re-renders don't stack pins on the map.
    if (!markerRef.current) {
      markerRef.current = new (google.maps as any).marker.AdvancedMarkerElement({
        map: mapRef.current,
        position: { lat, lng },
        title,
      })
    } else {
      markerRef.current.position = { lat, lng }
      markerRef.current.title = title
      markerRef.current.map = mapRef.current
    }
  }, [lat, lng, title, zoom])

  return <div ref={ref} style={{ height: '240px', width: '100%' }} />
}

export function DeviceLocationMap(props: Props) {
  function render(status: Status) {
    return (
      <div className="h-[240px] flex items-center justify-center bg-gray-50 text-xs text-gray-400">
        {status === Status.FAILURE ? 'Map unavailable' : 'Loading map…'}
      </div>
    )
  }

  return (
    <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} libraries={['marker']}>
      <StaticMap {...props} />
    </Wrapper>
  )
}
